import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import EmailVerifyModal from "../components/EmailVerifyModal";
import { RULES_VERSION } from "../lib/rules";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifyCtx, setVerifyCtx] = useState(null);   // conta que precisa confirmar o e-mail antes de entrar

  useEffect(() => {
    const token = localStorage.getItem("pokeot_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setAccount(r.data.data))
      .catch(() => { localStorage.removeItem("pokeot_token"); })
      .finally(() => setLoading(false));
  }, []);

  // Retorna a conta; ou "2FA" quando a conta exige o código do autenticador.
  // Retorna "VERIFY_EMAIL" quando falta confirmar o e-mail (abre a janela do código).
  const login = async (username, password, captcha_id = null, captcha_value = null, totp_code = null, remember = false) => {
    let data;
    const tdKey = "pkx_td:" + String(username || "").trim().toLowerCase();   // computador lembrado (2FA), por conta
    let deviceToken = null;
    try { deviceToken = localStorage.getItem(tdKey) || localStorage.getItem("pkx_td_last"); } catch (e) { /* sem storage */ }   // digitou o e-mail em vez do usuário (ou vice-versa)? usa o último computador lembrado; o servidor confere de qual conta é
    try {
      ({ data } = await api.post("/auth/login", {
        username,
        password,
        captcha_id,
        captcha_value,
        totp_code: totp_code || undefined,
        device_token: deviceToken || undefined,
        remember_device: !!(remember && totp_code)
      }));
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 403 && d && d.code === "EMAIL_NOT_VERIFIED") {
        setVerifyCtx({ username, password, emailHint: d.email_hint, autoSent: true });
        return "VERIFY_EMAIL";
      }
      throw err;
    }
    if (data.require_2fa) return "2FA";
    try {
      if (data.device_token) { localStorage.setItem(tdKey, data.device_token); localStorage.setItem("pkx_td_last", data.device_token); }
      else if (deviceToken && totp_code && !remember) { localStorage.removeItem(tdKey); }
    } catch (e) { /* sem storage */ }
    localStorage.setItem("pokeot_token", data.token);
    setAccount(data.account);
    return data.account;
  };

  const register = async (username, email, password, captcha_id = null, captcha_value = null, ref_code = null, accept = {}) => {
    const { data } = await api.post("/auth/register", {
      username,
      email,
      password,
      captcha_id,
      captcha_value,
      ref_code: ref_code || undefined,
      accept_terms: !!accept.p1,
      accept_rmt_terms: !!accept.p2,
      terms_version: RULES_VERSION,
      lang: (() => { try { return localStorage.getItem("lang") || undefined; } catch { return undefined; } })()
    });
    if (data.requires_email_verification) {
      setVerifyCtx({ username, password, emailHint: data.email_hint, autoSent: true });
      return "VERIFY_EMAIL";
    }
    localStorage.setItem("pokeot_token", data.token);
    setAccount(data.account);
    return data.account;
  };

  const logout = () => {
    localStorage.removeItem("pokeot_token");
    setAccount(null);
  };

  const refreshAccount = async () => {
    try {
      const r = await api.get("/auth/me");
      setAccount(r.data.data);
    } catch (e) {
      logout();
    }
  };

  // Depois de confirmar o código, entra sozinho (se a conta tiver 2FA, manda para a tela de login pedir o código do app)
  const afterVerified = async () => {
    const c = verifyCtx;
    setVerifyCtx(null);
    if (!c) return;
    try {
      const r = await login(c.username, c.password);
      if (r === "2FA") { toast.info("Agora entre com o código do seu aplicativo autenticador."); window.location.assign("/login"); }
      else if (r && r !== "VERIFY_EMAIL") window.location.assign("/panel");
    } catch (e) { window.location.assign("/login"); }
  };

  return (
    <AuthContext.Provider value={{ account, loading, login, register, logout, setAccount, refreshAccount }}>
      {children}
      {verifyCtx && <EmailVerifyModal ctx={verifyCtx} onClose={() => setVerifyCtx(null)} onVerified={afterVerified} />}
    </AuthContext.Provider>
  );
}

export { AuthContext };
export const useAuth = () => useContext(AuthContext);
